import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const HIDDEN_CASE_STUDY_SLUGS = new Set(["kaan-air", "one8"]);

const visibleCaseStudies = (caseStudies: CaseStudy[]) =>
  caseStudies.filter((caseStudy) => !HIDDEN_CASE_STUDY_SLUGS.has(caseStudy.slug));

export interface CaseStudy {
  id: string;
  title: string;
  slug: string;
  subtitle?: string;
  role?: string;
  year?: string;
  thumbnail_url?: string;
  hero_image_url?: string;
  accent_color?: string;
  is_featured?: boolean;
  is_published?: boolean;
  order_index?: number;
  duration?: string;
  team?: string;
  scope?: string;
  context?: string;
  summary?: string;
  client_name?: string;
}

export const useCaseStudies = () => {
  return useQuery({
    queryKey: ["case-studies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_studies")
        .select("*")
        .eq("is_published", true)
        .order("order_index", { ascending: true });

      if (error) throw error;
      return visibleCaseStudies(data as CaseStudy[]);
    },
  });
};

export const useFeaturedCaseStudies = () => {
  return useQuery({
    queryKey: ["featured-case-studies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_studies")
        .select("*")
        .eq("is_published", true)
        .eq("is_featured", true)
        .order("order_index", { ascending: true })
        .limit(6);

      if (error) throw error;
      return visibleCaseStudies(data as CaseStudy[]);
    },
  });
};

export const useNonFeaturedCaseStudies = () => {
  return useQuery({
    queryKey: ["non-featured-case-studies"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_studies")
        .select("*")
        .eq("is_published", true)
        .eq("is_featured", false)
        .order("order_index", { ascending: true });

      if (error) throw error;
      return visibleCaseStudies(data as CaseStudy[]);
    },
  });
};

export const useCaseStudy = (slug: string) => {
  return useQuery({
    queryKey: ["case-study", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("case_studies")
        .select("*")
        .eq("slug", slug)
        .eq("is_published", true)
        .maybeSingle();

      if (error) throw error;
      return data as CaseStudy | null;
    },
    enabled: !!slug && slug !== "demo",
  });
};

export const useCaseStudyNavigation = (currentSlug: string) => {
  const { data: allCaseStudies } = useCaseStudies();

  if (!allCaseStudies || allCaseStudies.length === 0) {
    return { prev: null, next: null };
  }

  const currentIndex = allCaseStudies.findIndex((cs) => cs.slug === currentSlug);
  
  if (currentIndex === -1) {
    return { prev: null, next: null };
  }

  const prev = currentIndex > 0 ? allCaseStudies[currentIndex - 1] : null;
  const next = currentIndex < allCaseStudies.length - 1 ? allCaseStudies[currentIndex + 1] : null;

  return { prev, next };
};
